import CallMadeIcon from '@mui/icons-material/CallMade';
import CallMissedIcon from '@mui/icons-material/CallMissed';
import CallMissedOutgoingIcon from '@mui/icons-material/CallMissedOutgoing';
import CallReceived from '@mui/icons-material/CallReceived';
import GraphicEq from '@mui/icons-material/GraphicEq';
import PlayCircleOutlineOutlinedIcon from '@mui/icons-material/PlayCircleOutlineOutlined';
import { Box, Theme, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  GridRenderCellParams,
  GridSortModel,
  GridValueFormatterParams,
} from '@mui/x-data-grid';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import moment from 'moment';
import React, { useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useRecoilStateLoadable, useRecoilValueLoadable } from 'recoil';

import { secondsDecimalFormatter } from '../../../../common/helperFunctions/minuteFormatter';
import CrmIcon from '../../../../images/crm-icon.svg';
import {
  IBusinessModel,
  IUserAtom,
  Role,
  user,
} from '../../../../recoil/atoms/user';
import { methodNameEnum } from '../../../../recoil/selectors/makePHPRequest';
import {
  IPermissions,
  permissionsSelector,
} from '../../../../recoil/selectors/permissionsSelector';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../../../services/rest/makeAPIRequest';
import {
  DataTable,
  IColDef,
} from '../../../modules/Manage/pages/queues-table/DataTable';
import CallTranscriptionModal from '../../modals/CallTranscriptionModal';
import { ModalWithStyles } from '../../modals/ModalWithStyles';

interface RecordingModalInfo {
  recordingUrl: string;
  agent: string;
  callId: string;
  callType: string;
  callDate: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  fullWidth: {
    width: '100% !important',
  },
  alignRight: {
    justifyContent: 'flex-end',
  },
  callMadeIcon: {
    color: '#26c281',
  },
  playIcon: {
    color: '#e87e04',
    marginRight: theme.spacing(1),
  },
  callMissedIcon: {
    color: '#d91e18',
  },
  messageIcon: {
    color: '#3598dc',
    marginRight: theme.spacing(1),
  },
  linkIcon: {
    color: '#337ab7',
    marginRight: theme.spacing(1),
  },
  phoneNumber: {
    '& .historyc2c.link.pointer': {
      display: 'block',
    },
  },
  audio: {
    transition: 'all 0.5s linear',
    boxShadow: '0px 0px 0px 0px',
    backgroundColor: '#f0f3f4',
    borderRadius: '7px 7px 7px 7px',
    height: 32,
    maxWidth: '-webkit-fill-available',
    margin: '0px auto',
    '&:focus': {
      outline: 0,
    },
  },
  audioContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  audioDetails: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    paddingLeft: '35px',
  },
  audioBullet: {
    width: '50%',
  },
  typeColumn: {
    minWidth: '100px !important',
    maxWidth: '100px !important',
    '&.MuiDataGrid-cell': {
      wordBreak: 'keep-all !important',
    },
  },
}));
const renderCell = (params: GridRenderCellParams) => (
  <div dangerouslySetInnerHTML={{ __html: params.value as string }}></div>
);

const getInitialSortModel = ({
  isOutboundOrPositiveCallsShown,
  hasCallTranscriptionProduct,
}: {
  isOutboundOrPositiveCallsShown: boolean;
  hasCallTranscriptionProduct: boolean;
}): GridSortModel =>
  hasCallTranscriptionProduct
    ? [
        {
          field: 'transcriptionStrength',
          sort: isOutboundOrPositiveCallsShown ? 'desc' : 'asc',
        },
      ]
    : [
        {
          field: 'duration',
          sort: 'desc',
        },
      ];

const CallReview = (props) => {
  const { callValue, dateInterval } = props;
  const { id } = useParams<{ id: string }>();
  const [userAtomLoadable, _setUserAtom] = useRecoilStateLoadable(user);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;
  const classes = useStyles();
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const { role, callRecord } = permissionsLoadable.contents as IPermissions;
  const [openRecordingModal, setOpenRecordingModal] = useState(false);
  const handleOpenRecordingModal = () => setOpenRecordingModal(true);
  const handleCloseRecordingModal = () => setOpenRecordingModal(false);
  const isOutboundOrPositiveCallsShown = callValue === 0;
  const defaultValue = isOutboundOrPositiveCallsShown ? -Infinity : Infinity;
  const [sortModel, setGridSortModel] = useState<GridSortModel>(
    getInitialSortModel({
      isOutboundOrPositiveCallsShown,
      hasCallTranscriptionProduct: props.hasCallTranscriptionProduct,
    })
  );
  const [recordingInfo, setRecordingInfo] = useState<RecordingModalInfo>({
    recordingUrl: '',
    agent: '',
    callId: '',
    callDate: '',
    callType: '',
  });
  const handleGridSortModelChange = (params: GridSortModel) => {
    setGridSortModel(params);
  };
  const [_, callReviewLoadable] = makeApiRequestCached(
    {
      method: 'post',
      stage: 'itn',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      endpoint: endpointEnum.GET_CALL_REVIEW,
      payloadJsonString: JSON.stringify({
        userid: id,
        businessid: userAtomContents?.businessid,
        interval: dateInterval,
        hasCallTranscriptionProduct: props.hasCallTranscriptionProduct,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result ?? cached.current
  );
  const [businessDataCached, businessDataLoadable] = makeApiRequestCached<
    IBusinessModel | undefined
  >(
    {
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_DATA,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.[0] ?? cached.current
  );
  const businessUsersLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_USERS,
      }),
    })
  );

  const callReviewData = useMemo(() => {
    if (callReviewLoadable?.state !== 'hasValue') return [];

    if (props.hasCallTranscriptionProduct) {
      return callValue === 0
        ? callReviewLoadable?.contents?.response?.data?.positiveCalls ?? []
        : callReviewLoadable?.contents?.response?.data?.negativeCalls ?? [];
    }

    return callValue === 0
      ? callReviewLoadable?.contents?.response?.data?.outbound ?? []
      : callReviewLoadable?.contents?.response?.data?.inbound ?? [];
  }, [callReviewLoadable]);

  const rowsCached = useRef([]);
  rowsCached.current = React.useMemo(
    () =>
      callReviewData?.length
        ? callReviewData?.map((call, i) => {
            const callType = call?._source?.calltype;
            const insideName = call?._source?.calleridName;
            const outsideName =
              call?._source?.fname ?? '' + ' ' + call?._source?.lname;
            const insideNumber =
              callType === 'outgoing'
                ? call?._source?.fromnumber164
                : call?._source?.tonumber164;
            const outsideNumber =
              callType === 'outgoing'
                ? call?._source?.tonumber164
                : call?._source?.fromnumber164;
            const inside = `<span class='callhistory-name'>${insideName}</span><span class='historyc2c link pointer'>${insideNumber}</span>`;
            const outside = `<span class='callhistory-name'>${outsideName}</span><span class='historyc2c link pointer'>${outsideNumber}</span>`;
            const callDate = moment(call?._source?.calldate as string).format(
              'M/D/YYYY, H:mm A'
            );
            const phpcalldate2 = `<span title='${callDate}'>${callDate}</span>`;
            const calldate = (call?._source?.calldate as string).split(' ');
            const date = calldate.length > 0 ? calldate[0] : '';
            const time = calldate.length > 0 ? calldate[1] : '';
            const callDateTime =
              calldate.length > 0 ? date + 'T' + time + 'Z' : '';
            const transcriptionStrength = call?._source?.transcriptionStrength;
            const transcriptionStatus = call?._source?.transcriptionStatus;
            return {
              amount: call?._source?.amount,
              businessid: call?._source?.businessid,
              calldate: callDateTime,
              calleridName: call?._source?.calleridName,
              callid: call?._source?.callid,
              callstatus: call?._source?.callstatus,
              calltype: callType,
              calluserid: call?._source?.calluserid,
              disposition: call?._source?.disposition,
              duration: call?._source?.duration, //todo change to minutes
              externalid: call?._source?.externalid,
              id: call?._source?.externalid,
              inside: inside,
              insidename: insideName,
              insidenumber: insideNumber,
              outside: outside,
              outsidenumber: outsideNumber,
              recordingurl: call?._source?.recordingurl,
              crmlink:
                call?._source?.crmlink ?? call?._source?.crmdata?.crmlink ?? '',
              phpcalldate2: phpcalldate2,
              transcriptionStrength,
              transcriptionStatus,
            };
          })
        : [],
    [callReviewData]
  );

  const [callForTranscriptionModal, setCallForTranscriptionModal] = useState<{
    externalid: string;
    calldate: string;
    calltype: string;
    recordingurl: string;
  }>();

  const columns = useMemo<IColDef[]>(
    () => [
      {
        field: '_event',
        headerName: 'Event',
        width: 65,
        disableExport: true,
        enableSorting: false,
        renderCell: (params: GridRenderCellParams) => {
          return params.row.calltype === 'incoming' ? (
            params.row?.callstatus === 'answered' ? (
              <Tooltip title="Incoming Answered">
                <CallReceived className={classes.callMadeIcon} />
              </Tooltip>
            ) : (
              <Tooltip title="Incoming Missed">
                <CallMissedIcon className={classes.callMissedIcon} />
              </Tooltip>
            )
          ) : params.row?.callstatus === 'answered' ? (
            <Tooltip title="Outgoing Answered">
              <CallMadeIcon className={classes.callMadeIcon} />
            </Tooltip>
          ) : (
            <Tooltip title="Outgoing Missed">
              <CallMissedOutgoingIcon className={classes.callMissedIcon} />
            </Tooltip>
          );
        },
      },
      {
        field: 'calldate',
        headerName: 'Date',
        valueFormatter: (params: GridValueFormatterParams) =>
          DateTime.fromISO(params.value as string, {
            zone: businessDataCached.current?.timezone_php,
          }).toLocaleString(DateTime.DATETIME_SHORT),
        headerClassName: classes.fullWidth,
        cellClassName: classes.fullWidth,
        align: 'left',
        headerAlign: 'left',
        minWidth: 175,
        maxWidth: 250,
      },
      {
        field: 'callid',
        headerName: 'Call ID',
        hide: role !== Role.SUPERADMIN,
        disableExport: true,
        headerClassName: clsx(classes.typeColumn) + ' superStyle',
        cellClassName: clsx(classes.typeColumn),
        minWidth: 100,
        maxWidth: 115,
      },
      {
        field: 'externalid',
        headerName: 'External ID',
        minWidth: 350,
        hide: role !== Role.SUPERADMIN,
        disableExport: true,
        headerAlign: 'left',
        align: 'left',
        headerClassName: classes.fullWidth + ' superStyle',
        cellClassName: classes.fullWidth,
        maxWidth: 600,
        renderCell: (params: GridRenderCellParams) => (
          <a
            style={{ textAlign: 'left' }}
            href={`https://console.plivo.com/voice/logs/calls/stats/${
              params.value
            }/?end_date=${moment(+(params.row.calldate as string)).format(
              'MM/DD/YYYY'
            )}&subaccount=SANZVLNGY4NDFHYMZHMJ`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {params.value}
          </a>
        ),
      },
      {
        field: 'transcriptionStrength',
        headerName: 'Score',
        headerClassName: clsx(classes.typeColumn),
        cellClassName: clsx(classes.typeColumn),
        minWidth: 100,
        maxWidth: 115,
        sortComparator: (a, b) => {
          // sort undefined values to the end of the list
          return (
            (typeof a === 'number' ? a : defaultValue) -
            (typeof b === 'number' ? b : defaultValue)
          );
        },
      },
      {
        field: 'calltype',
        headerName: 'Type',
        headerClassName: clsx(classes.typeColumn),
        cellClassName: clsx(classes.typeColumn),
        minWidth: 100,
        maxWidth: 115,
      },
      {
        field: 'callstatus',
        headerName: 'Status',
        headerClassName: clsx(classes.typeColumn),
        cellClassName: clsx(classes.typeColumn),
        minWidth: 100,
        maxWidth: 115,
      },
      {
        field: 'disposition',
        headerName: 'Disposition',
        headerClassName: classes.fullWidth,
        cellClassName: classes.fullWidth,
        minWidth: 150,
        maxWidth: 300,
      },
      {
        field: 'duration',
        headerName: 'Duration',
        minWidth: 60,
        maxWidth: 100,
        valueFormatter: (params: GridValueFormatterParams) =>
          secondsDecimalFormatter(params.value?.toString()),
        renderCell: (params: GridRenderCellParams) => (
          <span data-pup={`row-${params.id}-duration`}>
            {secondsDecimalFormatter(params.value?.toString())}
          </span>
        ),
      },
      {
        field: 'inside',
        headerName: 'Internal Call ID',
        renderCell,
        headerClassName: classes.fullWidth,
        cellClassName: clsx(classes.fullWidth, classes.phoneNumber),
        minWidth: 250,
        maxWidth: 500,
      },
      {
        field: 'outside',
        headerName: 'External Contact',
        renderCell,
        headerClassName: classes.fullWidth,
        cellClassName: clsx(classes.fullWidth, classes.phoneNumber),
        minWidth: 250,
        maxWidth: 500,
      },
      {
        field: 'amount',
        headerName: 'Amount',
        minWidth: 80,
        maxWidth: 150,
        renderCell: (params: GridRenderCellParams) => (
          <div>
            {Number(params?.value) % 1 !== 0
              ? Number(params?.value).toFixed(4)
              : Number(params?.value)}
          </div>
        ),
      },
      {
        field: 'pcActions',
        enableSorting: false,
        headerName: 'Actions',
        width: 115,
        headerAlign: 'right',
        align: 'right',
        cellClassName: classes.alignRight,
        disableExport: true,
        renderCell: (params: GridRenderCellParams) => (
          <div>
            {props.hasCallTranscriptionProduct &&
              params.row.transcriptionStatus === 'completed' && (
                <Tooltip title="Conversation Intelligence">
                  <GraphicEq
                    style={{ marginRight: '8px', cursor: 'pointer' }}
                    onClick={() => {
                      setCallForTranscriptionModal({
                        externalid: params.row.externalid,
                        calldate: params.row.calldate,
                        calltype: params.row.calltype,
                        recordingurl: params.row.recordingurl,
                      });
                      return false;
                    }}
                  >
                    Conversation Intelligence
                  </GraphicEq>
                </Tooltip>
              )}
            {params.row.crmlink && (
              <a
                href={params.row.crmlink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Tooltip title="Go to CRM">
                  <img
                    src={CrmIcon}
                    style={{ display: 'inline-block', width: '28px' }}
                    alt="crm icon"
                  />
                </Tooltip>
              </a>
            )}
            {params.row.recordingurl &&
              callRecord &&
              businessUsersLoadable.contents?.response?.row?.result?.find(
                (user: any) => +user?.userid === +params.row.calluserid
              )?.othersCanViewRecordings === 1 && (
                <a
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.preventDefault();
                    handleOpenRecordingModal();
                    setRecordingInfo({
                      agent: params.row.insidename,
                      recordingUrl: params.row.recordingurl,
                      callId: params.row.callid,
                      callType: params.row.calltype,
                      callDate: params.row.phpcalldate2.slice(
                        params.row.phpcalldate2.indexOf('>') + 1,
                        params.row.phpcalldate2.lastIndexOf('<')
                      ),
                    });
                  }}
                >
                  <Tooltip title="Play Audio">
                    <PlayCircleOutlineOutlinedIcon
                      className={classes.playIcon}
                    />
                  </Tooltip>
                </a>
              )}
          </div>
        ),
      },
    ],
    [classes, businessUsersLoadable, role, callRecord, businessDataLoadable]
  );

  return (
    <>
      <div style={{ marginTop: '24px' }}>
        <DataTable
          rows={rowsCached?.current ?? []}
          columns={columns}
          rowCount={rowsCached?.current?.length}
          page={1}
          paginationMode="server"
          sortModel={sortModel}
          onSortModelChange={handleGridSortModelChange}
          loading={callReviewLoadable.state === 'loading'}
        />
        <ModalWithStyles
          show={openRecordingModal}
          onHide={handleCloseRecordingModal}
          title="Call Audio"
          size="xs"
          submitLabel=""
        >
          <Box className={classes.audioContainer}>
            <div className={classes.audioDetails}>
              <p className={classes.audioBullet}>
                Agent: <strong>{recordingInfo.agent}</strong>
              </p>
              <p className={classes.audioBullet}>
                Call ID: <strong>{recordingInfo.callId}</strong>
              </p>
              <p className={classes.audioBullet}>
                Call Type: <strong>{recordingInfo.callType}</strong>
              </p>
              <p className={classes.audioBullet}>
                Date: <strong>{recordingInfo.callDate}</strong>
              </p>
            </div>
            <audio
              controls
              src={recordingInfo.recordingUrl}
              className={classes.audio}
            >
              Your browser does not support the audio element.
            </audio>
          </Box>
        </ModalWithStyles>
      </div>
      {props.hasCallTranscriptionProduct && (
        <CallTranscriptionModal
          show={!!callForTranscriptionModal}
          handleClose={() => {
            setCallForTranscriptionModal(undefined);
          }}
          callXd={callForTranscriptionModal?.externalid ?? ''}
          businessId={userAtomContents?.businessid}
          timezone={businessDataCached.current?.timezone_php}
          callDate={callForTranscriptionModal?.calldate ?? ''}
          callType={callForTranscriptionModal?.calltype ?? ''}
          recordingUrl={callForTranscriptionModal?.recordingurl ?? ''}
        />
      )}
    </>
  );
};

export default CallReview;
